import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, InputAdornment, IconButton, FormControlLabel, Checkbox, Button } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import coverPic from "../../assets/images/Hrms.png";
import Loader from '../../common/spinner';
import { postData, getData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";
import { setAuthData } from "../../utils/auth";

function LoginPage() {
    const navigate = useNavigate();
    const [creds, setCreds] = useState({ email: "", password: "" });
    const [ui, setUi] = useState({
        loading: false,
        error: null,
        showPass: false,
        remember: false
    });

    /**
     * Helper function to handle role-based navigation
     * Wrapped in useCallback to prevent unnecessary effect triggers and fix warnings
     */
    const redirectByRole = useCallback((user) => {
        const userRole = user.user_type.toLowerCase().replace(/\s+/g, '-');
        const allowedRoles = ["manager", "hr", "employee", "team-lead"];

        if (allowedRoles.includes(userRole)) {
            navigate(`/${userRole}/dashboard`);
        } else {
            navigate("/dashboard");
        }
    }, [navigate]);

    /**
     * LOGIC: AUTOMATIC RECOGNITION
     */
    useEffect(() => {
        const checkAuth = async () => {
            const persistentAuth = localStorage.getItem("persistentAuth");
            if (persistentAuth) {
                try {
                    const { token, user } = JSON.parse(persistentAuth);
                    // 1. Re-hydrate the auth utility/context
                    setAuthData({ token, user });

                    // 2. Validate token by fetching user details
                    try {
                        await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${user.id}`);
                        // If successful, perform Automatic Redirect
                        redirectByRole(user);
                    } catch (error) {
                        // If token is invalid, clear persistent auth
                        localStorage.removeItem("persistentAuth");
                    }
                } catch (e) {
                    // If data is corrupted, clear it
                    localStorage.removeItem("persistentAuth");
                }
            }
        };
        checkAuth();
    }, [redirectByRole]);

    const inputStyle = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
            borderRadius: '4px',
            color: '#000',
            '& fieldset': { borderColor: '#ccc' },
            '&:hover fieldset': { borderColor: '#1a237e' },
            '&.Mui-focused fieldset': { borderColor: '#1a237e' },
        },
        '& .MuiInputBase-input::placeholder': {
            color: '#666',
            opacity: 1,
        }
    };

    const handleLogin = async () => {
        // Validation
        if (!creds.email || !creds.password) {
            setUi(prev => ({ ...prev, error: "Please enter both Email and Password" }));
            return;
        }

        setUi(prev => ({ ...prev, loading: true, error: null }));

        try {
            const response = await postData(API_ENDPOINTS.userLogin, {
                email: creds.email.trim().toLowerCase(),
                password: creds.password
            });

            if (response.status === "SUCCESS") {
                const { accessToken, user } = response.message;

                /**
                 * LOGIC: PERSISTENCE
                 */
                if (ui.remember) {
                    localStorage.setItem("persistentAuth", JSON.stringify({
                        token: accessToken,
                        user: user
                    }));
                } else {
                    localStorage.removeItem("persistentAuth");
                }

                // Normal auth flow
                setAuthData({ token: accessToken, user: user });
                redirectByRole(user);

            } else {
                setUi(prev => ({ ...prev, error: response.message?.msg || "Invalid credentials" }));
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message?.msg ||
                err.response?.data?.message ||
                "Server connection failed. Please try again.";

            setUi(prev => ({ ...prev, error: errorMsg }));
        } finally {
            setUi(prev => ({ ...prev, loading: false }));
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, height: "100vh", bgcolor: "#292929", position: "relative" }}>

            {ui.loading && <Loader />}

            <Box sx={{ flex: 1, backgroundImage: `url(${coverPic})`, backgroundSize: "cover", backgroundPosition: "center" }} />

            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                <Box sx={{ width: "90%", maxWidth: "350px", textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#fff" }}>Account Login</Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: "#bbb" }}>
                        If you are already a member you can login with your user ID and password.
                    </Typography>

                    {ui.error && (
                        <Typography variant="body2" sx={{ mb: 2, color: "#ff6b6b", p: 1, bgcolor: "rgba(255,107,107,0.1)", borderRadius: 1 }}>
                            {ui.error}
                        </Typography>
                    )}

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="User ID / Email"
                        value={creds.email}
                        onChange={e => setCreds(prev => ({ ...prev, email: e.target.value }))}
                        sx={inputStyle}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        type={ui.showPass ? "text" : "password"}
                        placeholder="Password"
                        value={creds.password}
                        onChange={e => setCreds(prev => ({ ...prev, password: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        sx={inputStyle}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setUi(prev => ({ ...prev, showPass: !prev.showPass }))}
                                        edge="end"
                                        sx={{ color: "#bbb" }}
                                    >
                                        {ui.showPass ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22 }} />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={ui.remember}
                                onChange={e => setUi(prev => ({ ...prev, remember: e.target.checked }))}
                                sx={{ color: "#fff", '&.Mui-checked': { color: "#fff" } }}
                            />
                        }
                        label={<Typography variant="caption" sx={{ color: "#bbb" }}>Remember me</Typography>}
                        sx={{ mb: 2, width: '100%', display: 'flex', justifyContent: 'flex-start' }}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleLogin}
                        disabled={ui.loading}
                        sx={{ bgcolor: "#1a237e", fontWeight: "bold", "&:hover": { bgcolor: "#0d47a1" }, textTransform: 'none', height: '40px' }}
                    >
                        {ui.loading ? "Logging in..." : "Login"}
                    </Button>

                    <Typography
                        variant="caption"
                        sx={{
                            mt: 2,
                            color: "#bbb",
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "flex-start",
                            "&:hover": { color: "#fff", textDecoration: "underline" }
                        }}
                        onClick={() => navigate("/forgot-password")}
                    >
                        Forgot Password?
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default LoginPage;


// const handleLogin = async () => {
//         // Validation
//         if (!creds.email || !creds.password) {
//             setUi(prev => ({ ...prev, error: "Please enter both Email and Password" }));
//             return;
//         }

//         setUi(prev => ({ ...prev, loading: true, error: null }));

//         try {
//             const response = await postData(API_ENDPOINTS.userLogin, {
//                 identifier: creds.email.trim().toLowerCase(),
//                 password: creds.password
//             });

//             // Updated check: API now returns response.status and response.result
//             if (response.status === "SUCCESS") {
//                 const { accessToken, user } = response.result; // Changed from response.message

//                 /**
//                  * LOGIC: PERSISTENCE
//                  */
//                 if (ui.remember) {
//                     localStorage.setItem("persistentAuth", JSON.stringify({
//                         token: accessToken,
//                         user: user
//                     }));
//                 } else {
//                     localStorage.removeItem("persistentAuth");
//                 }

//                 // Normal auth flow
//                 setAuthData({ token: accessToken, user: user });
//                 redirectByRole(user);

//             } else {
//                 // Fallback for failed status using the new message string location
//                 setUi(prev => ({ ...prev, error: response.message || "Invalid credentials" }));
//             }
//         } catch (err) {
//             // Updated error handling to look for the message string in the response
//             const errorMsg = err.response?.data?.message ||
//                 "Server connection failed. Please try again.";

//             setUi(prev => ({ ...prev, error: errorMsg }));
//         } finally {
//             setUi(prev => ({ ...prev, loading: false }));
//         }
//     };